import {
    getScopeTopicsWithClauses,
    IScope,
    IScopeTopicWithClauses,
} from "@/api";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { SheetRef } from "react-modal-sheet";
import { FoundToggleState } from "../../types";
import { TopicAccordion } from "./TopicAccordion";

interface ScopeTopicsV2Props {
    scope: IScope;
    search: string;
    foundToggle: FoundToggleState;
    setFilteredClausesCount: (count: number) => void;
    pageNavigationPluginInstance: PageNavigationPlugin;
    pdfViewerLoaded: boolean;
    bottomSheetRef: React.MutableRefObject<SheetRef | undefined>;
    /** The scope_clause.id to highlight (unique per topic) */
    highlightedScopeClauseId?: number | null;
    onClauseSelect?: (scopeClauseId: number | null) => void;
    /** Topic ID to scroll to (from URL query param) */
    scrollToTopicId?: number | null;
}

/**
 * Main wrapper component for V2 Topics UI
 * Fetches topics with clauses and handles filtering
 */
export const ScopeTopicsV2 = (props: ScopeTopicsV2Props) => {
    const {
        scope,
        search,
        foundToggle,
        setFilteredClausesCount,
        pageNavigationPluginInstance,
        highlightedScopeClauseId,
        onClauseSelect,
        scrollToTopicId,
    } = props;

    // Track collapsed topics - all topics are expanded by default
    const [collapsedTopicIds, setCollapsedTopicIds] = useState<Set<number>>(
        new Set()
    );

    // Fetch topics with clauses using React Query
    const {
        data: topicsData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["scope-topics-with-clauses", scope.id],
        queryFn: () => getScopeTopicsWithClauses(scope.id),
        enabled: !!scope.id,
    });

    // Filter topics based on search and foundToggle
    const filteredTopics = useMemo(() => {
        const topics = topicsData?.topics || [];
        if (!topics.length) return [];

        return topics
            .map((topic) => {
                // Filter clauses within sections based on foundToggle
                const filteredSections = topic.sections.map((section) => {
                    const filteredClauses = section.clauses.filter((clause) => {
                        // Apply foundToggle filter
                        if (foundToggle === FoundToggleState.FOUND) {
                            return clause.found;
                        } else if (foundToggle === FoundToggleState.NOT_FOUND) {
                            return !clause.found;
                        }
                        return true;
                    });

                    return {
                        ...section,
                        clauses: filteredClauses,
                    };
                });

                // Apply search filter
                if (search) {
                    const searchLower = search.toLowerCase();

                    // Check if topic name matches
                    const topicNameMatches = topic.name
                        .toLowerCase()
                        .includes(searchLower);

                    // Check if any clause title matches
                    const clauseMatches = filteredSections.some((section) =>
                        section.clauses.some((clause) =>
                            clause.clause.title
                                .toLowerCase()
                                .includes(searchLower)
                        )
                    );

                    if (!topicNameMatches && !clauseMatches) {
                        return null;
                    }

                    // If only clause matches, filter sections to show only matching clauses
                    if (!topicNameMatches) {
                        const filteredSectionsBySearch = filteredSections.map(
                            (section) => ({
                                ...section,
                                clauses: section.clauses.filter((clause) =>
                                    clause.clause.title
                                        .toLowerCase()
                                        .includes(searchLower)
                                ),
                            })
                        );

                        return {
                            ...topic,
                            sections: filteredSectionsBySearch,
                        };
                    }
                }

                return {
                    ...topic,
                    sections: filteredSections,
                };
            })
            .filter((topic): topic is IScopeTopicWithClauses => {
                if (topic === null) return false;

                // Check if topic has any clauses after filtering
                const hasFilteredClauses = topic.sections.some(
                    (section) => section.clauses.length > 0
                );

                // Hide topic if it has no clauses matching the current filter
                return hasFilteredClauses;
            });
    }, [topicsData?.topics, search, foundToggle]);

    // Calculate total unique clause count (grouped by clause.id across all topics)
    const totalUniqueClauseCount = useMemo(() => {
        const uniqueClauseIds = new Set<number>();
        filteredTopics.forEach((topic) => {
            topic.sections.forEach((section) => {
                section.clauses.forEach((scopeClause) => {
                    uniqueClauseIds.add(scopeClause.clause.id);
                });
            });
        });
        return uniqueClauseIds.size;
    }, [filteredTopics]);

    // Update filtered clauses count
    useEffect(() => {
        const totalFilteredClauses = filteredTopics.reduce((acc, topic) => {
            const topicClauseCount = topic.sections.reduce(
                (sectionAcc, section) => sectionAcc + section.clauses.length,
                0
            );
            return acc + topicClauseCount;
        }, 0);

        setFilteredClausesCount(totalFilteredClauses);
    }, [filteredTopics, setFilteredClausesCount]);

    // Scroll to topic when scrollToTopicId is set (from URL query param)
    useEffect(() => {
        if (!scrollToTopicId || !topicsData?.topics) return;

        // Ensure the topic is expanded (not collapsed)
        setCollapsedTopicIds((prev) => {
            const next = new Set(prev);
            next.delete(scrollToTopicId);
            return next;
        });

        // Scroll to the topic element after a brief delay to allow rendering
        const timeoutId = setTimeout(() => {
            const element = document.getElementById(`topic-${scrollToTopicId}`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [scrollToTopicId, topicsData?.topics]);

    // Handle accordion toggle - toggle collapsed state
    const handleToggle = (topicId: number) => {
        const isCurrentlyExpanded = !collapsedTopicIds.has(topicId);

        // If collapsing and this topic contains the highlighted clause, clear the selection
        if (isCurrentlyExpanded && highlightedScopeClauseId && onClauseSelect) {
            const topic = topicsData?.topics?.find((t) => t.id === topicId);
            if (topic) {
                const containsHighlightedClause = topic.sections.some(
                    (section) =>
                        section.clauses.some(
                            (clause) => clause.id === highlightedScopeClauseId
                        )
                );
                if (containsHighlightedClause) {
                    onClauseSelect(null);
                }
            }
        }

        setCollapsedTopicIds((prev) => {
            const next = new Set(prev);
            if (next.has(topicId)) {
                next.delete(topicId);
            } else {
                next.add(topicId);
            }
            return next;
        });
    };

    // Auto-expand topic when a clause is highlighted from PDF (ensure it's not collapsed)
    useEffect(() => {
        if (highlightedScopeClauseId && topicsData?.topics) {
            // Find the topic containing the highlighted scope clause
            const topicWithClause = topicsData.topics.find((topic) =>
                topic.sections.some((section) =>
                    section.clauses.some(
                        (clause) => clause.id === highlightedScopeClauseId
                    )
                )
            );

            if (topicWithClause && collapsedTopicIds.has(topicWithClause.id)) {
                // Remove from collapsed set to expand it
                setCollapsedTopicIds((prev) => {
                    const next = new Set(prev);
                    next.delete(topicWithClause.id);
                    return next;
                });
            }
        }
    }, [highlightedScopeClauseId, topicsData?.topics, collapsedTopicIds]);

    // Loading state
    if (isLoading) {
        return (
            <Stack
                px={2}
                pb={2}
                bgcolor="background.paper"
                gap={1}
                alignItems="center"
            >
                <CircularProgress />
            </Stack>
        );
    }

    // Error state
    if (error) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ p: 2 }}>
                <Typography color="error">
                    Failed to load topics. Please try again.
                </Typography>
            </Stack>
        );
    }

    // Empty state when no topics match filters
    if (filteredTopics.length === 0) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ p: 2 }}>
                <SearchOffIcon
                    sx={{
                        fontSize: 48,
                        color: "text.secondary",
                        mb: 2,
                    }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No results found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {search && `No topics or clauses match "${search}"`}
                    {!search &&
                        foundToggle === FoundToggleState.NOT_FOUND &&
                        "No not found clauses"}
                    {!search &&
                        foundToggle === FoundToggleState.FOUND &&
                        "No found clauses"}
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack>
            {filteredTopics.map((topic) => (
                <TopicAccordion
                    key={topic.id}
                    topic={topic}
                    expanded={!collapsedTopicIds.has(topic.id)}
                    onToggle={() => handleToggle(topic.id)}
                    pageNavigationPluginInstance={pageNavigationPluginInstance}
                    highlightedScopeClauseId={highlightedScopeClauseId}
                    onClauseSelect={onClauseSelect}
                    totalUniqueClauseCount={totalUniqueClauseCount}
                />
            ))}
        </Stack>
    );
};
